import { useEffect, useState } from 'react';
import type { Section } from './types';

export type TranslateOverlayProps = {
    isOpen: boolean;
    targetSectionId?: string | null;
    queryLanguage: string | null;
    loading: boolean;
    error: string | null;
    translations: Record<string, string> | string;
    sections: Section[];
    onClose: () => void;
    onApply: () => void;
    onTranslate: (s: Section) => void;
};

export default function TranslateOverlay({
    isOpen,
    targetSectionId,
    queryLanguage,
    loading,
    error,
    translations,
    sections,
    onClose,
}: TranslateOverlayProps) {
    const [visible, setVisible] = useState(isOpen);
    const [animating, setAnimating] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setVisible(true);
            setTimeout(() => setAnimating(true), 10);
        } else if (visible) {
            setAnimating(false);
            setTimeout(() => setVisible(false), 300);
        }
    }, [isOpen]);

    if (!visible) return null;

    const getTranslation = (sectionId: string) => {
        if (typeof translations === 'string') return translations;
        return translations[sectionId] || '—';
    };

    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 transition-opacity duration-300 ${animating ? 'opacity-100' : 'opacity-0'}`}
        >
            <div
                className={`w-full max-w-4xl rounded-lg bg-white p-6 shadow-lg transition-all duration-300 ${animating ? 'scale-100 translate-y-0' : 'scale-95 translate-y-8'}`}
            >
                <div className="flex items-start justify-between gap-4">
                    <h2 className="text-lg font-semibold">
                        {queryLanguage ? queryLanguage.charAt(0).toUpperCase() + queryLanguage.slice(1) : ''}
                    </h2>
                    <button type="button" className="rounded-md bg-gray-100 px-3 py-1 text-sm" onClick={onClose}>
                        Close
                    </button>
                </div>

                <div className="mt-4 max-h-[60vh] space-y-4 overflow-y-auto bg-[#f5f5f5] p-3">
                    {loading && <div className="text-sm text-gray-600">Please wait...</div>}
                    {error && <div className="text-sm text-red-600">{error}</div>}
                    {!loading && (
                        <div className="space-y-3">
                            {targetSectionId ? (
                                sections
                                    .filter((s) => s.section == (targetSectionId || ''))
                                    .map((s) => (
                                        <div key={s.section} className="rounded-md border border-gray-100">
                                            <div className="font-semibold">Section {s.section}</div>
                                            <div className="mt-2 text-gray-700">{getTranslation(s.section)}</div>
                                        </div>
                                    ))
                            ) : (
                                <div className="text-sm text-gray-800">
                                    No section specified in query params.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
